import { Controller } from "../vendor/stimulus.js";

export default class extends Controller {
  static values = { url: { type: String, default: "/events" } };

  connect() {
    this.shouldConnect = true;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.setupConnection();
  }

  disconnect() {
    this.shouldConnect = false;
    this.eventSource?.close();
  }

  async setupConnection() {
    const baseUrl = document.body.dataset.baseUrl || '';

    // Initialize session first to establish cookie in browser context
    await fetch(baseUrl + '/session', { credentials: 'include' });

    // Check if controller disconnected during async setup
    if (!this.shouldConnect) return;

    // Connect SSE with the session cookie
    this.eventSource = new EventSource(baseUrl + this.urlValue, { withCredentials: true });
    this.eventSource.onmessage = (e) => this.handleMessage(e);
    this.eventSource.onopen = () => this.handleOpen();
    this.eventSource.onerror = (e) => this.handleError(e);
  }

  handleOpen() {
    this.reconnectAttempts = 0;
  }

  handleError(event) {
    if (this.eventSource?.readyState === EventSource.CLOSED) {
      this.attemptReconnect();
    }
  }

  attemptReconnect() {
    if (!this.shouldConnect) return;

    this.reconnectAttempts++;

    if (this.reconnectAttempts > this.maxReconnectAttempts) {
      window.dispatchEvent(new CustomEvent('xhr-failed', {
        detail: { reason: 'SSE connection lost' }
      }));
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 16000);

    setTimeout(() => {
      if (this.shouldConnect) {
        this.setupConnection();
      }
    }, delay);
  }

  handleMessage(event) {
    const { id, html, target } = JSON.parse(event.data);

    if (target === 'upload-section') {
      // Replace the upload section content (approval section, welcome section, or loading)
      const uploadSection = document.getElementById('upload-section');
      if (uploadSection) {
        uploadSection.outerHTML = html;
      }
      // Show moodboard section if it exists
      document.querySelector('.section-moodboard')?.classList.remove('hidden');
      return;
    }

    if (target === 'moodboard') {
      const moodboardEl = document.getElementById('moodboard');
      document.querySelector('.section-moodboard')?.classList.remove('hidden');

      // Try surgical update if moodboard exists with images
      if (moodboardEl && this.updateMoodboard(moodboardEl, html)) {
        this.enableChatForm();
        return;
      }

      // First load: simple replacement
      if (moodboardEl) {
        moodboardEl.outerHTML = html;
      }

      // Set up fade-in handlers for first load images
      const newMoodboard = document.getElementById('moodboard');
      if (newMoodboard) {
        // Force animation restart for shimmer effect
        // (CSS animations may not play if started while element was hidden)
        const moodboardInner = newMoodboard.querySelector('.moodboard.is-loading');
        if (moodboardInner) {
          moodboardInner.style.animation = 'none';
          moodboardInner.offsetHeight; // trigger reflow
          moodboardInner.style.animation = '';
        }

        newMoodboard.querySelectorAll('img').forEach(img => {
          if (img.complete) {
            img.classList.add('img-is-loaded');
          } else {
            img.onload = () => img.classList.add('img-is-loaded');
            img.onerror = () => img.classList.add('img-is-loaded');
          }
        });
        this.enableChatForm();
      }
      return;
    }

    if (id === 0) {
      const qrEl = document.getElementById('qr-code-placeholder');
      if (qrEl) {
        qrEl.outerHTML = html;
      }
      return;
    }

    const conversation = document.getElementById('conversation');
    if (!conversation) return;

    // Remove all messages with ID >= this one (backend is authoritative)
    if (id > 0) {
      this.removeMessagesFromId(conversation, id);
    }

    // First message triggers intro collapse
    if (conversation.children.length === 0) {
      this.collapseIntroSections();
    }

    // Insert the new message
    conversation.insertAdjacentHTML('beforeend', html);
    conversation.hidden = false;
    conversation.removeAttribute('hidden');

    // Scroll to the new message
    const insertedEl = conversation.lastElementChild;
    if (insertedEl) {
      // Get the actual visible element (skip display:contents wrapper)
      const scrollTarget = insertedEl.querySelector('section.outlet, section') || insertedEl;

      // Use requestAnimationFrame to ensure DOM is rendered before scrolling
      requestAnimationFrame(() => {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else {
      this.scrollToBottom();
    }
  }

  removeMessagesFromId(container, fromId) {
    const toRemove = [];
    container.querySelectorAll('[id^="msg-"]').forEach(el => {
      const msgId = parseInt(el.id.replace('msg-', ''), 10);
      if (!isNaN(msgId) && msgId >= fromId) {
        toRemove.push(el);
      }
    });
    toRemove.forEach(el => el.remove());
  }

  collapseIntroSections() {
    document.querySelector('main')?.classList.add('collapse-intro-sections');
  }

  enableChatForm() {
    const form = document.querySelector('.section-moodboard form[action="/chat"]');
    if (form) {
      form.classList.remove('disable-form');
      form.querySelectorAll('button[type="submit"]').forEach(el => el.disabled = false);
    }
  }

  scrollToBottom() {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({ top: maxScroll, behavior: 'smooth' });
  }

  /**
   * Update moodboard colors and images instead of full replacement.
   * Returns true if surgical update was performed, false if full replacement needed.
   */
  updateMoodboard(moodboardEl, newHtml) {
    const moodboard = moodboardEl.querySelector('.moodboard') || moodboardEl;
    const existingImages = moodboard.querySelectorAll('img[data-img-id]');

    // No existing images = first load, need full replacement
    if (existingImages.length === 0) {
      return false;
    }

    // Parse incoming HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(newHtml, 'text/html');

    // Update color swatches
    ['primary', 'secondary', 'tertiary', 'quarternary'].forEach(colorType => {
      const newColor = doc.querySelector(`[data-color="${colorType}"]`);
      const existingColor = moodboard.querySelector(`[data-color="${colorType}"]`);
      if (newColor && existingColor) {
        existingColor.setAttribute('style', newColor.getAttribute('style') || '');
      }
    });

    // Update images - track how many need loading
    const newImages = doc.querySelectorAll('img[data-img-id]');
    if (newImages.length === 0) {
      return false;
    }

    let pendingLoads = 0;
    const checkAllLoaded = () => {
      pendingLoads--;
      if (pendingLoads <= 0) {
        moodboard.classList.remove('is-loading');
      }
    };

    newImages.forEach(newImg => {
      const imgId = newImg.getAttribute('data-img-id');
      const existingImg = moodboard.querySelector(`img[data-img-id="${imgId}"]`);
      if (!existingImg) return;

      const newSrc = newImg.getAttribute('src');
      const currentSrc = existingImg.getAttribute('src');

      if (currentSrc !== newSrc) {
        pendingLoads++;
        existingImg.classList.remove('img-is-loaded');
        existingImg.src = newSrc;
        existingImg.onload = () => {
          existingImg.classList.add('img-is-loaded');
          checkAllLoaded();
        };
        existingImg.onerror = () => {
          existingImg.classList.add('img-is-loaded');
          checkAllLoaded();
        };
      }
      // If same src, image is already visible - do nothing
    });

    // Handle QR code separately (doesn't have data-img-id)
    const newQrCode = doc.querySelector('#qr-code-image');
    const existingQrCode = moodboard.querySelector('#qr-code-image');

    if (newQrCode && existingQrCode) {
      // Update existing QR code src
      const newSrc = newQrCode.getAttribute('src');
      if (existingQrCode.getAttribute('src') !== newSrc) {
        existingQrCode.src = newSrc;
        existingQrCode.classList.add('img-is-loaded');
      }
    } else if (newQrCode && !existingQrCode) {
      // QR code doesn't exist yet - find the qr-code anchor container and insert
      const newQrAnchors = doc.querySelectorAll('.qr-code');
      newQrAnchors.forEach(newQrAnchor => {
        // Find the parent block in the new HTML and match it in existing moodboard
        const parentBlock = newQrAnchor.closest('.block-3, .block-4');
        if (parentBlock) {
          const blockClass = parentBlock.classList.contains('block-3') ? '.block-3' : '.block-4';
          const targetBlock = moodboard.querySelector(blockClass);
          if (targetBlock) {
            targetBlock.insertBefore(newQrAnchor.cloneNode(true), targetBlock.firstChild);
          }
        }
      });
    }

    // If no images changed, just remove loading state
    if (pendingLoads === 0) {
      moodboard.classList.remove('is-loading');
    }

    return true;
  }
}
